import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/PageTitle";
import MealLog from "../components/MealLog";
import TodayTotals from "../components/TodayTotals";
import PromptModal from "../components/PromptModal";
import { HistorySkeleton } from "../components/Skeletons";
import { deleteMealRemote, loadMealsRemote, localDateOf, saveMealRemote } from "../lib/storage";
import { saveFavoriteFromMeal } from "../lib/favorites";
import { friendlyError } from "../lib/errors";
import { sumTotals, type LoggedMeal } from "../lib/types";

export default function History() {
  const navigate = useNavigate();
  const [meals, setMeals] = useState<LoggedMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentlyLoggedSourceId, setRecentlyLoggedSourceId] = useState<string | null>(null);
  const [pendingFavorite, setPendingFavorite] = useState<{ meal: LoggedMeal; suggested: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setMeals(await loadMealsRemote());
      setError(null);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!recentlyLoggedSourceId) return;
    const t = setTimeout(() => setRecentlyLoggedSourceId(null), 1500);
    return () => clearTimeout(t);
  }, [recentlyLoggedSourceId]);

  const byDay = useMemo(() => groupByDay(meals), [meals]);

  async function onDelete(id: string) {
    try {
      await deleteMealRemote(id);
      await refresh();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  function onEdit(id: string) {
    navigate(`/edit/${id}`);
  }

  function onFavorite(meal: LoggedMeal) {
    // Suggest a name from the items list — better than just the first item
    // when the meal is multi-ingredient. Capped so the modal input doesn't
    // pre-fill with a war-and-peace caption.
    const names = meal.estimate.items.map((i) => i.name).filter(Boolean);
    const suggested =
      names.length === 0
        ? "Favorite meal"
        : names.length === 1
        ? names[0]
        : `${names[0]} + ${names.length - 1} more`;
    setPendingFavorite({ meal, suggested: suggested.slice(0, 60) });
  }

  async function confirmFavorite(name: string | null) {
    const pending = pendingFavorite;
    setPendingFavorite(null);
    if (!pending || !name) return;
    try {
      await saveFavoriteFromMeal(pending.meal, name);
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  async function onLogAgain(source: LoggedMeal) {
    try {
      // Re-logged meals don't carry over the photo (it's bound to the original
      // meal's storage path). Macros + items + meal type are preserved.
      await saveMealRemote({
        meal_type: source.meal_type,
        estimate: source.estimate,
        caption: source.caption,
        source_providers: source.source_providers,
        disagreement_pct: source.disagreement_pct
      });
      await refresh();
      setRecentlyLoggedSourceId(source.id);
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="space-y-6">
      <div className="p-4 pb-0">
        <PageTitle>History</PageTitle>
      </div>

      {loading && <HistorySkeleton />}

      {error && (
        <div className="mx-4 p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      )}

      {!loading && byDay.length === 0 ? (
        <div className="p-8 text-center text-slate-500">No meals yet. Log one on the Today tab.</div>
      ) : null}

      {pendingFavorite && (
        <PromptModal
          title="Save as favorite"
          description="Pick a short name — you'll be able to quick-log this in one tap from now on."
          defaultValue={pendingFavorite.suggested}
          placeholder="e.g. Morning oatmeal"
          confirmLabel="Save favorite"
          onClose={confirmFavorite}
          maxLength={80}
        />
      )}

      {!loading && byDay.length > 0 && (
        byDay.map(({ date, items }) => (
          <section key={date}>
            <div className="px-4 sticky top-0 bg-slate-950/95 backdrop-blur py-2 z-10 flex items-baseline justify-between">
              <span className="text-base font-semibold text-slate-100">{formatDate(date)}</span>
              <span className="text-xs text-slate-500 tabular-nums">
                <span className="text-slate-300">{Math.round(sumTotals(items).calories)}</span> kcal · {items.length} {items.length === 1 ? "entry" : "entries"}
              </span>
            </div>
            <div className="mt-2">
              <TodayTotals totals={sumTotals(items)} mealCount={items.length} />
            </div>
            <MealLog
              meals={items}
              onDelete={onDelete}
              onLogAgain={onLogAgain}
              onEdit={onEdit}
              onFavorite={onFavorite}
              recentlyLoggedSourceId={recentlyLoggedSourceId}
            />
          </section>
        ))
      )}
    </div>
  );
}

function groupByDay(meals: LoggedMeal[]): Array<{ date: string; items: LoggedMeal[] }> {
  const groups = new Map<string, LoggedMeal[]>();
  for (const m of meals) {
    // Use LOCAL date so evening meals don't get split into "tomorrow"
    // (their UTC date), same fix as mealsOnDate + computeStreak.
    const day = localDateOf(m.logged_at);
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day)!.push(m);
  }
  return Array.from(groups.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, items]) => ({ date, items }));
}

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayMs = 24 * 60 * 60 * 1000;
  const diff = Math.round((today.getTime() - d.getTime()) / dayMs);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
